import { Checkbox } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { RevisionFieldProps } from 'components/forms/revision/fill-out/types';
import {
  IFormInputSpecComparator,
  IFormInputSpecComparatorOperator,
} from 'pages/revisions-module/template-editor/editors/form/types';

const BINARY_OPERATORS: IFormInputSpecComparatorOperator[] = [
  'eq',
  'ne',
  'gt',
  'lt',
  'gte',
  'lte',
  'logical-and',
  'logical-or',
];

/**
 * Converts unknown values to boolean for logical operators.
 */
function toBooleanValue(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return !isNaN(value) && value !== 0;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() !== '';
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return Boolean(value);
}

/**
 * Checks whether a value should be considered empty.
 */
function isEmptyValue(value: unknown) {
  if (value === undefined || value === null) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
}

/**
 * Evaluates the comparator operator on provided values.
 */
function compareValues(operator: IFormInputSpecComparatorOperator, fieldA: unknown, fieldB: unknown): boolean {
  switch (operator) {
    case 'empty':
      return isEmptyValue(fieldA);

    case 'not-empty':
      return !isEmptyValue(fieldA);

    case 'eq':
      return String(fieldA ?? '') === String(fieldB ?? '');

    case 'ne':
      return String(fieldA ?? '') !== String(fieldB ?? '');

    case 'gt':
    case 'lt':
    case 'gte':
    case 'lte': {
      // This also handles Date objects, as they will be converted to their timestamp number, i.e. Date.now()
      const numericA = Number(fieldA);
      const numericB = Number(fieldB);

      if (!isNaN(numericA) && !isNaN(numericB)) {
        if (operator === 'gt') {
          return numericA > numericB;
        }

        if (operator === 'lt') {
          return numericA < numericB;
        }

        if (operator === 'gte') {
          return numericA >= numericB;
        }

        if (operator === 'lte') {
          return numericA <= numericB;
        }

        /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
        return false;
      }

      const stringA = String(fieldA ?? '');
      const stringB = String(fieldB ?? '');

      if (operator === 'gt') {
        return stringA > stringB;
      }

      if (operator === 'lt') {
        return stringA < stringB;
      }

      if (operator === 'gte') {
        return stringA >= stringB;
      }

      if (operator === 'lte') {
        return stringA <= stringB;
      }

      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return false;
    }

    case 'logical-and':
      return toBooleanValue(fieldA) && toBooleanValue(fieldB);

    case 'logical-or':
      return toBooleanValue(fieldA) || toBooleanValue(fieldB);

    case 'logical-not':
      return !toBooleanValue(fieldA);

    default:
      return false;
  }
}

/**
 * Field used to evaluate boolean comparison between two form fields.
 */
export default function RevisionFieldComparator({
  spec,
  name,
  deviceId,
}: RevisionFieldProps<IFormInputSpecComparator>) {
  const { form, findInputByName } = useFillOutRevisionDataProvider();

  const fieldAInput = useMemo(() => findInputByName(spec.fieldA), [findInputByName, spec.fieldA]);
  const fieldBInput = useMemo(() => findInputByName(spec.fieldB || ''), [findInputByName, spec.fieldB]);

  /**
   * Returns the source field value according to its form context.
   */
  const getInputValue = (inputName: string, context?: 'revision' | 'device') => {
    if (!inputName) {
      return undefined;
    }

    if (context === 'revision') {
      return form.values.fields[inputName];
    }

    if (context === 'device' && deviceId !== undefined) {
      return form.values.devices[String(deviceId)]?.fields?.[inputName];
    }

    return undefined;
  };

  const fieldAValue = getInputValue(spec.fieldA, fieldAInput?.context as 'revision' | 'device' | undefined);

  const fieldBValue = BINARY_OPERATORS.includes(spec.operator)
    ? getInputValue(spec.fieldB || '', fieldBInput?.context as 'revision' | 'device' | undefined)
    : undefined;

  const evaluated = compareValues(spec.operator, fieldAValue, fieldBValue);

  useEffect(() => {
    const currentValue = form.getInputProps(name, { type: 'checkbox' }).checked;

    if (currentValue !== evaluated) {
      form.setFieldValue(name, evaluated);
    }
  }, [evaluated, form, name]);

  return (
    <Checkbox
      size="lg"
      name={name}
      label={spec.label}
      required={spec.required}
      disabled
      readOnly
      checked={evaluated}
      onChange={() => undefined}
    />
  );
}
